// M1 – Bán hàng tại quầy (6.1, 6.4, 6.5)
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Button, ConfigProvider, Dropdown, Modal, Select, message } from 'antd';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import {
  AlertTriangle, ArrowLeft, CalendarClock, Crown, FileText, Grid, HandCoins, LayoutGrid, Loader2, LogOut, MapPin, Minus, Plus, ReceiptText, RotateCcw, Search, ShieldAlert, ShieldCheck,
  ShoppingBasket, ShoppingCart, Stethoscope, StickyNote, Tag, Trash2, User, Wifi, WifiOff, X,
} from 'lucide-react';
import { getPosAppointmentCart, verifyPosCheckin } from 'api/PosV2';
import {
  BRANCHES, TYPE_LABEL, type CartLine, type CatalogItem, type Customer, type DiscountType,
  type AppointmentSnapshot, type Draft, type PaymentLineInput,
} from './data';
import { compareWithAppointment, useWaitingAppointments } from './appointmentLink';
import { calcOrder } from './pricing';
import {
  APPROVER_LABEL, canApprove, cartFromApi, checkoutAppointmentRemote, createWalkInRemote, currentStaffId, customerFromApi, newRequestId,
  saveAppointmentServicesRemote, remoteMethods, requiredApprover, useRemoteProducts, useServerBranches, useStaffOptions, type StaffOption,
} from './remote';
import usePosStore from './store';
import { draftLockInfo, foldVi, formatPhone, money, usePosAuth, viFilterOption } from './utils';
import { DemoRoleSwitch, ROLE_LABEL, posTheme } from './components/PosShell';
import CustomerPicker from './components/CustomerPicker';
import PaymentModal from './components/PaymentModal';
import DraftsModal from './components/DraftsModal';
import WaitingPanel from './components/WaitingPanel';
import AppointmentDiff from './components/AppointmentDiff';
import InstallmentPlan, {
  MIN_FIRST_PCT, capPayments, firstPercent, planError, planNeedsApproval, planToApi, rescalePlan, splitPlan, type PlanRow,
} from './components/InstallmentPlan';
import DiscountValueInput from './components/DiscountValueInput';
import useTreatmentStore from '../Treatment/store';
import { pricingOf } from '../Treatment/data';
import PosCardDebt from '../Treatment/PosCardDebt';
import PosPayRequests from '../Treatment/PosPayRequests';
import RemoteOrderModal, { type RemoteOrderResult } from './components/RemoteOrderModal';


const DISCOUNT_TYPE_OPTIONS: { value: DiscountType; label: string }[] = [{ value: '%', label: '%' }, { value: 'đ', label: 'đ' }];
const NO_ORDER_DISCOUNT: { type: DiscountType; value: number } = { type: '%', value: 0 };

/** Giỏ đang soạn lưu theo tab trình duyệt (xem "giữ giỏ đang soạn khi rời màn") */
const CART_SESSION_KEY = 'khothi-pos-cart-session';
/** Chi nhánh hệ thống ghi nhận đơn bán lẻ – nhớ theo máy quầy */
const SALE_BRANCH_KEY = 'khothi-pos-sale-branch';
interface CartSession {
  branchId: string;
  cart: CartLine[];
  customer: Customer | null;
  orderDiscount: { type: DiscountType; value: number };
  voucherCode: string;
  orderNote: string;
  plan: PlanRow[] | null;
  aptOriginal: CartLine[];
  aptSavedNote?: string;
  activeApt: AppointmentSnapshot | null;
  activeDraftId: string | null;
}
const readCartSession = (): CartSession | null => {
  try {
    const raw = sessionStorage.getItem(CART_SESSION_KEY);
    return raw ? (JSON.parse(raw) as CartSession) : null;
  } catch { return null; }
};
const writeCartSession = (s: CartSession) => {
  try { sessionStorage.setItem(CART_SESSION_KEY, JSON.stringify(s)); } catch { /* bộ nhớ đầy / bị chặn – bỏ qua */ }
};
const clearCartSession = () => {
  try { sessionStorage.removeItem(CART_SESSION_KEY); } catch { /* bỏ qua */ }
};
/** Đổi kiểu chiết khấu mà giữ nguyên số tiền đang giảm: 2.000.000đ trên 14.000.000 → 14,2857% (4 số lẻ để lệch không quá vài đồng) */
const convertDiscount = (to: DiscountType, amount: number, base: number) =>
  to === 'đ' ? amount : base > 0 ? Math.round((amount / base) * 1e6) / 1e4 : 0;

const newKey = (itemId: string) => `${itemId}-${Date.now()}-${Math.floor(Math.random() * 1e6)}`;
const isEditable = (el: Element | null) => !!el && (['INPUT', 'TEXTAREA', 'SELECT'].includes(el.tagName) || (el as HTMLElement).isContentEditable);
const anyModalOpen = () => Array.from(document.querySelectorAll('.ant-modal-wrap')).some(el => (el as HTMLElement).offsetParent !== null || getComputedStyle(el).display !== 'none');

function useOnline() {
  const [online, setOnline] = useState(typeof navigator === 'undefined' ? true : navigator.onLine);
  useEffect(() => {
    const on = () => setOnline(true);
    const off = () => setOnline(false);
    window.addEventListener('online', on);
    window.addEventListener('offline', off);
    return () => { window.removeEventListener('online', on); window.removeEventListener('offline', off); };
  }, []);
  return online;
}

export default function PosTerminal() {
  const navigate = useNavigate();
  const [params, setParams] = useSearchParams();
  const online = useOnline();
  const { role, realRole, isDemo, userName } = usePosAuth();
  const [modal, modalCtx] = Modal.useModal();

  const branchId = usePosStore(s => s.workingBranch);
  const setWorkingBranch = usePosStore(s => s.setWorkingBranch);
  const config = usePosStore(s => s.config);
  const customers = usePosStore(s => s.customers);
  const orders = usePosStore(s => s.orders);
  const drafts = usePosStore(s => s.drafts);
  const saveDraftToStore = usePosStore(s => s.saveDraft);
  const lockDraft = usePosStore(s => s.lockDraft);
  const unlockDraft = usePosStore(s => s.unlockDraft);
  const staffOptions = useStaffOptions();
  const treatmentPackages = useTreatmentStore(s => s.packages);
  // Bán lẻ tại quầy chỉ bán sản phẩm (dịch vụ / gói đi theo lịch hẹn) – danh mục lấy từ hệ thống (API chưa có tồn kho, danh mục)
  const products = useRemoteProducts();
  const CATALOG = products.items;
  const RETAIL = CATALOG;
  // Chi nhánh ghi nhận đơn bán lẻ (chi nhánh trên hệ thống, nhớ theo máy)
  const serverBranches = useServerBranches();
  const [saleBranchId, setSaleBranchId] = useState<number | null>(() => {
    try { return Number(localStorage.getItem(SALE_BRANCH_KEY)) || null; } catch { return null; }
  });
  const saleBranch = serverBranches.find(b => b.id === saleBranchId) || null;
  const chooseSaleBranch = (id: number) => {
    setSaleBranchId(id);
    try { localStorage.setItem(SALE_BRANCH_KEY, String(id)); } catch { /* chỉ là tiện ích nhớ lựa chọn */ }
  };

  // Danh mục
  const [query, setQuery] = useState('');
  const [debounced, setDebounced] = useState('');
  const loading = products.loading;
  const [mobileTab, setMobileTab] = useState<'catalog' | 'cart'>('catalog');

  // Giỏ hàng
  const [cart, setCart] = useState<CartLine[]>([]);
  const [customer, setCustomer] = useState<Customer | null>(null);
  // Bỏ chiết khấu đơn: luôn 0, kể cả khi mở nháp / phiên cũ có lưu CK đơn
  const orderDiscount = NO_ORDER_DISCOUNT;
  // Voucher chưa có API kiểm tra mã – tạm không áp dụng (giữ trường cho nháp / phiên cũ)
  const voucherCode = '';
  // Mã chống tạo trùng đơn bán lẻ: mỗi lần mở popup thanh toán một mã, bấm lại trong popup dùng lại mã đó
  const walkInRequestId = useRef('');
  const [orderNote, setOrderNote] = useState('');
  const [showOrderNote, setShowOrderNote] = useState(false);
  const [openNotes, setOpenNotes] = useState<Record<string, boolean>>({});
  const [expandedNames, setExpandedNames] = useState<Record<string, boolean>>({});
  const [flashKey, setFlashKey] = useState('');
  // Chia tổng đơn thành nhiều đợt: thu đợt 1 ngay, các đợt sau ghi nợ theo lịch
  const [plan, setPlan] = useState<PlanRow[] | null>(null);
  // Dòng gốc của lịch hẹn lúc nạp – để khôi phục dịch vụ lỡ xóa (đơn lịch hẹn không thêm hàng từ danh mục được)
  const [aptOriginal, setAptOriginal] = useState<CartLine[]>([]);
  // Ghi chú đơn đã lưu lên lịch hẹn (để biết ghi chú có thay đổi chưa cập nhật)
  const [aptSavedNote, setAptSavedNote] = useState('');
  const [activeDraftId, setActiveDraftId] = useState<string | null>(null);
  // Đang thu tiền cho lịch hẹn khách đã xác nhận làm
  const [activeApt, setActiveApt] = useState<AppointmentSnapshot | null>(null);
  // Cột trái: danh sách khách chờ thanh toán (mặc định) hoặc danh mục hàng để bán lẻ
  const [leftMode, setLeftMode] = useState<'waiting' | 'catalog'>('waiting');

  // Popup
  const [draftsOpen, setDraftsOpen] = useState(false);
  const [paymentOpen, setPaymentOpen] = useState(false);
  // Đơn đã tạo trên hệ thống (lịch hẹn hoặc bán lẻ – API V2) và popup tra đơn / thu nợ
  const [remoteResult, setRemoteResult] = useState<RemoteOrderResult | null>(null);
  const [lookupOpen, setLookupOpen] = useState(false);
  const [pickingId, setPickingId] = useState<string | null>(null);

  const searchRef = useRef<HTMLInputElement>(null);
  const customerRef = useRef<HTMLInputElement>(null);
  const orderNoteRef = useRef<HTMLTextAreaElement>(null);

  // ---------------------------------------------------------------- dữ liệu suy ra
  const debtOf = useCallback((cid: string) => orders.filter(o => o.customerId === cid && o.status !== 'cancelled').reduce((s, o) => s + o.debt, 0), [orders]);
  const workingBranchName = (BRANCHES.find(b => b.id === branchId)?.name || branchId).replace('Khothi ', '');
  // Đang thu tiền lịch hẹn trên hệ thống (ngược lại là bán lẻ – cũng tạo đơn trên hệ thống)
  const aptMode = !!activeApt?.remote;
  const activeDraftName = activeDraftId ? drafts.find(d => d.id === activeDraftId)?.name || '' : '';
  // KM tự động / voucher của POS còn là dữ liệu mẫu (chưa có API) nên không áp vào đơn thật
  const pricing = useMemo(
    () => calcOrder(cart, { branchId, promotions: [], orderDiscount, voucher: null, roundingUnit: config.roundingUnit }),
    [cart, branchId, orderDiscount, config.roundingUnit],
  );
  const totals = pricing.totals;
  // Server bắt buộc approved_by theo 3 cấp (≤10% thu ngân · ≤30% Quản lý · >30% Admin/BGĐ) – cả lịch hẹn lẫn bán lẻ.
  // Chưa có API duyệt bằng PIN nên chỉ người đăng nhập đủ thẩm quyền (và có gắn nhân viên) tự duyệt được.
  const remoteNeed = requiredApprover(pricing.manualPercent);
  const remoteCanApprove = !!remoteNeed && !isDemo && canApprove(realRole, remoteNeed);
  const remoteApprover = remoteCanApprove ? currentStaffId() : null;
  const remoteApprovalBlocked = !!remoteNeed && !remoteApprover;
  const remoteApprovalMsg = remoteNeed
    ? `Chiết khấu ${pricing.manualPercent.toFixed(1)}% cần ${APPROVER_LABEL[remoteNeed]} duyệt – ${remoteCanApprove
      ? 'tài khoản đang đăng nhập chưa gắn nhân viên nên chưa duyệt được'
      : `đăng nhập tài khoản ${APPROVER_LABEL[remoteNeed]} (có gắn nhân viên) để thanh toán`}`
    : '';
  // Chia đợt chỉ cho đơn lịch hẹn (dịch vụ giá trị lớn); bán lẻ sản phẩm thanh toán một lần
  const planBlockedReason =
    !aptMode ? 'Bán lẻ thanh toán một lần, không chia đợt' :
    !cart.length || totals.total <= 0 ? 'Giỏ hàng trống' :
    !activeApt?.customer ? 'Cần có khách hàng để chia đợt' : null;
  const collectNow = plan ? plan[0].amount : totals.total;
  const planHint = plan ? `Đợt 1/${plan.length} trên tổng đơn ${money(totals.total)}đ – còn ${money(totals.total - plan[0].amount)}đ chia ${plan.length - 1} đợt sau, hệ thống theo dõi hạn từng đợt. ` : '';
  // Trả góp có đợt 1 < 30%: server cần plan_approved_by (Quản lý). Như duyệt CK lịch hẹn: chỉ người đăng nhập đủ thẩm quyền
  // (và có gắn nhân viên) tự duyệt được
  const planNeedApproval = !!plan && planNeedsApproval(plan, totals.total);
  const planApprover = planNeedApproval && !isDemo && canApprove(realRole, 'manager') ? currentStaffId() : null;
  const planApprovalOk = !planNeedApproval || !!planApprover;
  const planFirstPct = plan ? Math.round(firstPercent(plan, totals.total) * 10) / 10 : 0;
  const totalQty = cart.reduce((s, l) => s + l.qty, 0);
  const branchDrafts = drafts.filter(d => d.branchId === branchId);
  const waiting = useWaitingAppointments();
  const waitingCount = waiting.items.length;
  const aptDiff = useMemo(
    () => (activeApt ? compareWithAppointment(activeApt, cart, totals) : null),
    [activeApt, cart, totals],
  );

  // Dropdown KTV đơn lịch hẹn: người đang gán trên lịch hẹn → phòng KTV → còn lại (API chưa có chi nhánh nên không lọc cứng)
  const staffGroups = useMemo(() => {
    const onApt = new Set((activeApt?.lines || []).map(l => l.staffId).filter(Boolean));
    const opt = (s: StaffOption) => ({ value: s.id, label: s.name, dept: s.dept });
    const pinned = staffOptions.filter(s => onApt.has(s.id));
    const rest = staffOptions.filter(s => !onApt.has(s.id));
    return [
      { label: 'Đang gán trên lịch hẹn', options: pinned.map(opt) },
      { label: 'Kỹ thuật viên', options: rest.filter(s => s.isKtv).map(opt) },
      { label: 'Nhân viên khác', options: rest.filter(s => !s.isKtv).map(opt) },
    ].filter(g => g.options.length);
  }, [staffOptions, activeApt]);
  const renderStaffOption = (o: { label?: React.ReactNode; data: { dept?: string } }) => (
    <div className="flex items-center justify-between gap-3">
      <span className="truncate">{o.label}</span>
      {o.data.dept && <span className="shrink-0 text-xs text-slate-400">{o.data.dept}</span>}
    </div>
  );

  // Chiết khấu dòng gói liệu trình không vượt mức tối đa quy định của gói
  const packageCapMsg = (() => {
    const over = cart.find(l => {
      if (l.type !== 'package' || !l.discountValue) return false;
      const pkg = treatmentPackages.find(p => p.id === l.itemId);
      if (!pkg) return false;
      const pct = l.discountType === '%' ? l.discountValue : l.discountValue / Math.max(1, l.listPrice * l.qty) * 100;
      return pct > pricingOf(pkg).maxDiscountPct + 1e-9;
    });
    if (!over) return null;
    const cap = pricingOf(treatmentPackages.find(p => p.id === over.itemId)).maxDiscountPct;
    return cap ? `${over.name} chỉ cho chiết khấu tối đa ${cap}%` : `${over.name} bán đúng giá, không cho chiết khấu`;
  })();

  const qtyInCart = (itemId: string) => cart.filter(l => l.itemId === itemId).reduce((s, l) => s + l.qty, 0);
  const stockOf = (item: { itemId?: string; id?: string }) => {
    const it = CATALOG.find(c => c.id === (item.itemId || item.id));
    return it?.stock ? it.stock[branchId] ?? 0 : null;
  };

  // Tìm theo tên (không dấu), mã hàng hoặc mã vạch
  const matches = (c: CatalogItem, q: string) =>
    foldVi(c.name).includes(foldVi(q)) || c.code.toLowerCase().includes(q) || (!!c.barcode && c.barcode.toLowerCase().includes(q));
  const filtered = useMemo(() => {
    const q = debounced.trim().toLowerCase();
    return q ? RETAIL.filter(c => matches(c, q)) : RETAIL;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [debounced, RETAIL]);

  // ---------------------------------------------------------------- hiệu ứng
  useEffect(() => { const t = setTimeout(() => setDebounced(query), 300); return () => clearTimeout(t); }, [query]);
  useEffect(() => {
    if (!flashKey) return;
    document.querySelector(`[data-line-key="${flashKey}"]`)?.scrollIntoView({ block: 'nearest', behavior: 'smooth' });
    const t = setTimeout(() => setFlashKey(''), 1000);
    return () => clearTimeout(t);
  }, [flashKey]);
  // Tổng đơn đổi (sửa giỏ, chiết khấu…) → giữ tỷ lệ các đợt
  useEffect(() => {
    if (!plan) return;
    // Rời đơn lịch hẹn sang bán lẻ (hoặc khôi phục giỏ bán lẻ cũ có kế hoạch đợt) → bỏ chia đợt
    if (!aptMode || totals.total <= 0) setPlan(null);
    else if (plan.reduce((s, r) => s + r.amount, 0) !== totals.total) setPlan(rescalePlan(plan, totals.total));
  }, [aptMode, totals.total, plan]);

  // ---------------------------------------------------------------- giỏ hàng
  const resetCart = () => {
    setCart([]);
    setCustomer(null);
    setOrderNote('');
    setShowOrderNote(false);
    setOpenNotes({});
    setExpandedNames({});
    setPlan(null);
    setAptOriginal([]);
    setAptSavedNote('');
    setActiveDraftId(null);
    setActiveApt(null);
  };

  const addItem = (item: CatalogItem) => {
    if (aptMode) {
      // Danh mục POS còn là dữ liệu mẫu, không có mã dịch vụ hệ thống để lưu lên lịch hẹn
      message.warning('Đang thu tiền lịch hẹn – chỉ điều chỉnh các dịch vụ trên lịch hẹn. Thoát đơn để bán lẻ.');
      return;
    }
    const stock = stockOf(item);
    if (config.blockOutOfStock && stock !== null && qtyInCart(item.id) + 1 > stock) {
      message.error(`${item.name} chỉ còn ${stock} tại chi nhánh`);
      return;
    }
    let key = '';
    setCart(prev => {
      const idx = prev.findIndex(l => l.itemId === item.id && !l.discountValue && !l.staffId && !l.note);
      if (idx > -1) {
        key = prev[idx].key;
        return prev.map((l, i) => (i === idx ? { ...l, qty: Math.min(999, l.qty + 1) } : l));
      }
      key = newKey(item.id);
      // Thêm vào cuối giỏ – cùng thứ tự với hóa đơn in ra; dòng mới tự cuộn tới và nháy sáng
      return [...prev, { key, itemId: item.id, code: item.code, name: item.name, type: item.type, listPrice: item.price, qty: 1, discountType: '%', discountValue: 0, staffId: '', note: '' }];
    });
    setTimeout(() => setFlashKey(key), 0);
  };

  const updateLine = (key: string, patch: Partial<CartLine>) => setCart(prev => prev.map(l => (l.key === key ? { ...l, ...patch } : l)));

  const setLineQty = (line: CartLine, qty: number) => {
    if (qty <= 0) return removeLine(line.key);
    updateLine(line.key, { qty: Math.min(999, Math.floor(qty)) });
  };

  /** Đưa dòng về giỏ ở đúng vị trí cũ (bỏ qua nếu dòng / dịch vụ lịch hẹn đó đã có lại) */
  const restoreLine = (line: CartLine, index: number) => setCart(prev =>
    prev.some(l => l.key === line.key || (!!line.aptServiceId && l.aptServiceId === line.aptServiceId))
      ? prev : [...prev.slice(0, index), line, ...prev.slice(index)]);

  /** Xóa ngay, không hỏi – thay bằng thông báo có nút Hoàn tác trong vài giây */
  const removeLine = (key: string) => {
    const index = cart.findIndex(l => l.key === key);
    if (index < 0) return;
    const line = cart[index];
    setCart(prev => prev.filter(l => l.key !== key));
    const msgKey = `remove-${key}`;
    message.open({
      key: msgKey, type: 'info', duration: 8,
      content: (
        <span>
          Đã xóa “{line.name.length > 40 ? `${line.name.slice(0, 40)}…` : line.name}”
          <button onClick={() => { restoreLine(line, index); message.destroy(msgKey); }} className="ml-3 font-semibold text-[#2c6b7f] underline underline-offset-2">Hoàn tác</button>
        </span>
      ),
    });
  };

  const switchLineDiscountType = (line: CartLine, to: DiscountType) => {
    if (to === line.discountType) return;
    const priced = pricing.lines.find(l => l.key === line.key);
    const base = (priced?.gross || 0) - (priced?.promoAmount || 0);
    updateLine(line.key, { discountType: to, discountValue: convertDiscount(to, priced?.lineDiscountAmount || 0, base) });
  };


  const setLineDiscount = (line: CartLine, raw: number) => {
    const priced = pricing.lines.find(l => l.key === line.key);
    const max = line.discountType === '%' ? 100 : Math.max(0, (priced?.gross || 0) - (priced?.promoAmount || 0));
    const value = Math.max(0, Math.min(raw, max));
    if (raw > max) message.warning(line.discountType === '%' ? 'Chiết khấu tối đa 100%' : `Chiết khấu tối đa ${money(max)}đ`);
    updateLine(line.key, { discountValue: value });
  };

  // Đơn lịch hẹn có thay đổi chưa "Cập nhật" lên hệ thống? So với bản đã lưu (lúc nạp / lần cập nhật gần nhất)
  const aptDirty = useMemo(() => {
    if (!aptMode) return false;
    const sig = (rows: string[]) => rows.sort().join(';');
    const current = sig(cart.map(l => {
      const off = pricing.lines.find(p => p.key === l.key)?.lineDiscountAmount || 0;
      return `${l.aptServiceId}|${l.qty}|${l.listPrice}|${l.staffId || ''}|${off}`;
    }));
    const saved = sig(aptOriginal.map(o => {
      const off = o.discountType === 'đ' ? o.discountValue : Math.round((o.listPrice * o.qty * o.discountValue) / 100);
      return `${o.aptServiceId}|${o.qty}|${o.listPrice}|${o.staffId || ''}|${off}`;
    }));
    return current !== saved || orderNote.trim() !== aptSavedNote;
  }, [aptMode, cart, pricing, aptOriginal, orderNote, aptSavedNote]);

  const leaveAppointment = (code?: string) => {
    if (activeDraftId) unlockDraft(activeDraftId);
    resetCart();
    setLeftMode('waiting');
    if (code) message.info(`Đã thoát đơn ${code} – đơn vẫn ở danh sách chờ thanh toán`);
  };

  /**
   * "Thoát đơn" (nút dưới, Esc, chuyển sang thẻ khách chờ khác): chỉ rời tiến trình thu tiền, đơn vẫn ở danh sách chờ.
   * Không có thay đổi chưa lưu thì thoát ngay; có thì hỏi: ở lại / bỏ thay đổi / cập nhật rồi thoát.
   */
  const exitAppointment = (next?: () => void) => {
    const code = activeApt?.code;
    const go = () => { leaveAppointment(next ? undefined : code); next?.(); };
    if (!aptDirty) return go();
    const dialog = modal.confirm({
      title: `Đơn ${code} có thay đổi chưa cập nhật`,
      content: (
        <div className="space-y-1.5 text-sm text-slate-600">
          <p>Dịch vụ, số lượng, chiết khấu, KTV hoặc ghi chú đã sửa nhưng chưa bấm <b>Cập nhật</b>. Thoát bây giờ sẽ bỏ các thay đổi này.</p>
          {plan && <p className="text-amber-700">Lịch chia đợt chỉ được lưu khi thanh toán nên sẽ không được giữ.</p>}
          <p className="text-slate-500">Đơn vẫn nằm trong danh sách chờ thanh toán.</p>
        </div>
      ),
      footer: (
        <div className="mt-4 flex flex-wrap justify-end gap-2">
          <Button onClick={() => dialog.destroy()}>Ở lại</Button>
          <Button danger onClick={() => { dialog.destroy(); go(); }}>Bỏ thay đổi</Button>
          <Button type="primary" onClick={async () => { dialog.destroy(); if (await updateAppointment()) go(); }}>Cập nhật rồi thoát</Button>
        </div>
      ),
    });
  };

  const cancelCart = () => {
    if (!cart.length) return;
    if (aptMode) return exitAppointment();
    modal.confirm({
      title: 'Hủy giỏ hàng này?',
      content: activeDraftId ? 'Đơn nháp vẫn được giữ lại, chỉ đóng khỏi màn hình.' : 'Toàn bộ hàng trong giỏ sẽ bị xóa.',
      okText: 'Hủy giỏ', okButtonProps: { danger: true }, cancelText: 'Quay lại',
      onOk: () => { if (activeDraftId) unlockDraft(activeDraftId); resetCart(); },
    });
  };

  // ---------------------------------------------------------------- nháp
  const saveDraft = () => {
    if (!cart.length) return;
    const now = new Date();
    const hhmm = `${String(now.getHours()).padStart(2, '0')}:${String(now.getMinutes()).padStart(2, '0')}`;
    // Lịch hẹn đã có nháp (chưa ai khác đang mở) thì cập nhật nháp đó, không sinh thêm nháp trùng
    const existing = activeDraftId
      ? drafts.find(d => d.id === activeDraftId)
      : activeApt
        ? drafts.find(d => d.appointment?.id === activeApt.id && d.branchId === branchId && !draftLockInfo(d, userName).locked)
        : undefined;
    const d = saveDraftToStore({
      id: existing?.id || activeDraftId,
      name: existing?.name || `${customer?.name || activeApt?.customer?.name || 'Khách'}${activeApt ? ` ${activeApt.code}` : ''} – ${hhmm}`,
      branchId, customerId: customer?.id || null, customer, lines: cart, orderDiscount, voucherCode, note: orderNote, total: totals.total, by: userName,
      appointment: activeApt,
    });
    resetCart();
    message.success(existing ? `Đã cập nhật đơn nháp “${d.name}”` : `Đã lưu đơn nháp “${d.name}”`);
  };

  /** Đơn chờ thanh toán (lịch hẹn) không có nháp: "Cập nhật" lưu dịch vụ / SL / KTV / CK / ghi chú lên lịch hẹn */
  const [updatingApt, setUpdatingApt] = useState(false);
  const updateAppointment = async (): Promise<boolean> => {
    if (!activeApt || !cart.length || updatingApt) return false;
    if (!online) { message.error('Mất kết nối – chưa thể cập nhật lịch hẹn'); return false; }
    const unpriced = cart.find(l => l.aptServiceId && l.listPrice <= 0);
    if (unpriced) { message.error(`Nhập đơn giá cho “${unpriced.name}” trước khi cập nhật`); return false; }
    setUpdatingApt(true);
    const note = orderNote.trim();
    const res = await saveAppointmentServicesRemote(activeApt.id, cart, pricing, note);
    setUpdatingApt(false);
    if (!res.ok) { message.error(res.error); return false; }
    setAptSavedNote(note);
    // Đồng bộ giỏ + bản chụp lịch hẹn theo hệ thống (đối chiếu tính từ bản vừa lưu)
    if (res.cart) {
      const { snapshot, lines } = cartFromApi(res.cart);
      setCart(lines);
      setAptOriginal(lines);
      setActiveApt(snapshot);
    }
    message.success(`Đã cập nhật lịch hẹn ${activeApt.code}`);
    waiting.reload();
    return true;
  };

  const openDraft = (d: Draft) => {
    const lock = draftLockInfo(d, userName);
    if (lock.locked) { message.error(`Đơn nháp đang được ${lock.by} xử lý`); return; }
    const load = () => {
      if (products.loading) { message.info('Đang tải danh mục sản phẩm – thử mở lại nháp sau giây lát'); return; }
      if (activeDraftId && activeDraftId !== d.id) unlockDraft(activeDraftId);
      // BR5.1: tính lại theo giá hiện hành, báo các dòng đổi giá; hàng không còn trên hệ thống thì bỏ khỏi giỏ
      const changed: { name: string; from: number; to: number }[] = [];
      const kept = d.lines.filter(l => l.aptServiceId || CATALOG.some(c => c.id === l.itemId));
      const dropped = d.lines.length - kept.length;
      const lines = kept
        .map(l => {
          const item = CATALOG.find(c => c.id === l.itemId);
          if (!item || l.aptServiceId) return l;
          const current = item.price;
          if (current !== l.listPrice) changed.push({ name: l.name, from: l.listPrice, to: current });
          return { ...l, listPrice: current };
        });
      lockDraft(d.id, userName);
      setCart(lines);
      setCustomer(customers.find(c => c.id === d.customerId) || d.customer || null);
      setOrderNote(d.note);
      setShowOrderNote(!!d.note);
      setActiveDraftId(d.id);
      setActiveApt(d.appointment || null);
      setDraftsOpen(false);
      setMobileTab('cart');
      if (dropped) message.warning(`${dropped} mặt hàng trong nháp không còn trên hệ thống – đã bỏ khỏi giỏ`);
      if (changed.length) {
        modal.info({
          title: 'Giá đã thay đổi so với lúc lưu nháp',
          content: (
            <ul className="mt-2 space-y-1 text-sm">
              {changed.map(c => <li key={c.name}><b>{c.name}</b>: {money(c.from)} → <b className="text-[#2c6b7f]">{money(c.to)}</b></li>)}
            </ul>
          ),
          okText: 'Đã hiểu',
        });
      } else {
        message.success(`Đã mở đơn nháp “${d.name}”`);
      }
    };
    // Nháp của lịch hẹn: lịch hẹn có thể đã được thu ở máy khác kể từ lúc lưu nháp
    const guardedLoad = async () => {
      if (d.appointment?.remote) {
        const paid = await verifyPosCheckin({ appointment_id: Number(d.appointment.id) });
        if (paid.ok && paid.data) {
          modal.confirm({
            title: 'Nháp không còn dùng được',
            content: `Lịch hẹn ${d.appointment.code} đã được thu ở đơn ${paid.data.order_code}. Xóa nháp này?`,
            okText: 'Xóa nháp', okButtonProps: { danger: true }, cancelText: 'Giữ lại',
            onOk: () => { usePosStore.getState().deleteDrafts([d.id]); message.success('Đã xóa đơn nháp'); },
          });
          return;
        }
        // Nháp cũ (đơn chờ thanh toán nay không còn lưu nháp): lấy lịch hẹn trên hệ thống làm bản gốc,
        // nội dung nháp là phần thay đổi chưa cập nhật – thu ngân bấm Cập nhật hoặc Thoát đơn để bỏ
        const res = await getPosAppointmentCart(d.appointment.id);
        if (!res.ok || !res.data) { message.error(`Không nạp được lịch hẹn ${d.appointment.code}: ${res.message}`); return; }
        const { snapshot, lines } = cartFromApi(res.data);
        if (activeDraftId) unlockDraft(activeDraftId);
        resetCart();
        setCart(d.lines.length ? d.lines : lines);
        setAptOriginal(lines);
        setOrderNote(d.note);
        setShowOrderNote(!!d.note);
        setActiveApt(snapshot);
        setLeftMode('waiting');
        setDraftsOpen(false);
        setMobileTab('cart');
        message.info(`Đã nạp lịch hẹn ${snapshot.code} kèm nội dung nháp – bấm Cập nhật để lưu lên lịch hẹn`);
        return;
      }
      load();
    };
    if (cart.length && activeDraftId !== d.id) {
      modal.confirm({ title: 'Mở đơn nháp?', content: 'Giỏ hàng hiện tại sẽ bị thay thế. Lưu nháp trước nếu cần giữ lại.', okText: 'Mở nháp', cancelText: 'Hủy', onOk: guardedLoad });
    } else guardedLoad();
  };

  // Mở nháp từ màn /pos/drafts (?draft=)
  useEffect(() => {
    const id = params.get('draft');
    if (!id) return;
    const d = drafts.find(x => x.id === id);
    params.delete('draft');
    setParams(params, { replace: true });
    if (!d) { message.error('Không tìm thấy đơn nháp (có thể đã hết hạn hoặc bị xóa)'); return; }
    if (d.branchId !== branchId) setWorkingBranch(d.branchId);
    openDraft(d);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [params]);

  // ---------------------------------------------------------------- khách chờ thanh toán (lịch hẹn S7 – API POS V2)
  /** Nạp giỏ hàng lịch hẹn từ hệ thống (API 2) */
  const pickAppointment = (apt: { id: string; code?: string }) => {
    const load = async () => {
      setPickingId(apt.id);
      const res = await getPosAppointmentCart(apt.id);
      setPickingId(null);
      if (!res.ok || !res.data) {
        message.error(`Không nạp được lịch hẹn ${apt.code || `#${apt.id}`}: ${res.message}`);
        return;
      }
      const { snapshot, lines } = cartFromApi(res.data);
      if (!lines.length) { message.warning(`Lịch hẹn ${snapshot.code} chưa có dịch vụ`); return; }
      if (activeDraftId) unlockDraft(activeDraftId);
      resetCart();
      setCart(lines);
      setAptOriginal(lines);
      setActiveApt(snapshot);
      setMobileTab('cart');
      message.success(`${snapshot.customer?.name || 'Khách'} – kiểm tra dịch vụ, KTV, chiết khấu rồi bấm Thanh toán`);
    };
    if (cart.length && activeApt?.id !== apt.id) {
      // Đang thu đơn lịch hẹn khác: chuyển luôn nếu không có thay đổi chưa cập nhật, có thì hỏi
      if (aptMode) return exitAppointment(load);
      modal.confirm({ title: 'Thu tiền cho khách này?', content: 'Giỏ hàng hiện tại sẽ bị thay thế. Lưu nháp trước nếu cần giữ lại.', okText: 'Thu tiền', cancelText: 'Hủy', onOk: load });
    } else load();
  };

  // Mở thẳng từ trang lịch hẹn (?appointment=ID)
  useEffect(() => {
    const id = params.get('appointment');
    if (!id) return;
    params.delete('appointment');
    setParams(params, { replace: true });
    setLeftMode('waiting');
    pickAppointment({ id });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [params]);

  // ---------------------------------------------------------------- thanh toán
  const pay = () => {
    if (!cart.length) return;
    if (!online) { message.error('Mất kết nối – chưa thể thanh toán. Hãy lưu nháp.'); return; }
    if (plan) {
      const err = planBlockedReason || planError(plan, totals.total)
        || (planApprovalOk ? null : `đợt 1 chỉ ${planFirstPct}% – dưới ${MIN_FIRST_PCT}% cần Quản lý duyệt`);
      if (err) { message.error(`Chia đợt thanh toán: ${err}`); setMobileTab('cart'); return; }
    }
    if (cart.some(l => l.type === 'package') && !customer) {
      message.error('Vui lòng chọn khách hàng cho gói liệu trình');
      customerRef.current?.focus();
      setMobileTab('cart');
      return;
    }
    if (packageCapMsg) { message.error(packageCapMsg); setMobileTab('cart'); return; }
    if (config.blockOutOfStock) {
      const over = cart.find(l => { const st = stockOf(l); return st !== null && qtyInCart(l.itemId) > st; });
      if (over) { message.error(`${over.name} vượt tồn kho chi nhánh`); return; }
    }
    const unpriced = cart.find(l => l.aptServiceId && l.listPrice <= 0);
    if (unpriced) { message.error(`Nhập đơn giá cho “${unpriced.name}” trước khi thanh toán`); setMobileTab('cart'); return; }
    if (aptMode && cart.some(l => !l.aptServiceId)) { message.error('Giỏ có dòng không thuộc lịch hẹn – xóa dòng đó trước khi thanh toán'); return; }
    if (!aptMode) {
      if (!saleBranch) { message.error('Chọn chi nhánh bán ở cạnh ô tìm sản phẩm trước khi thanh toán'); setMobileTab('catalog'); return; }
      const missing = cart.find(l => !CATALOG.some(c => c.id === l.itemId));
      if (missing) { message.error(`“${missing.name}” không có trên hệ thống – xóa khỏi giỏ và chọn lại`); setMobileTab('cart'); return; }
    }
    if (remoteApprovalBlocked) { message.error(remoteApprovalMsg); return; }
    if (totals.total <= 0) { message.error('Tổng đơn phải lớn hơn 0'); return; }
    // Mỗi lần mở popup một mã chống tạo trùng; bấm xác nhận lại trong popup dùng lại mã đó
    if (!aptMode) walkInRequestId.current = newRequestId();
    setPaymentOpen(true);
  };

  /** Đơn lịch hẹn: lưu dịch vụ → checkout → thu các dòng còn lại → hiện đơn trên hệ thống */
  const confirmRemotePayment = async (payments: PaymentLineInput[], note: string) => {
    if (!activeApt) return;
    const res = await checkoutAppointmentRemote({
      appointmentId: activeApt.id, cart, pricing, payments, cashier: userName, note,
      approvedBy: remoteApprover,
      approvedNote: remoteApprover && remoteNeed
        ? `${APPROVER_LABEL[remoteNeed]} ${userName} duyệt CK ${pricing.manualPercent.toFixed(1)}% tại POS`
        : undefined,
      // Trả góp: server lưu kế hoạch đợt và theo dõi hạn / quá hạn
      ...(plan ? { installments: planToApi(plan), planApprovedBy: planApprover } : {}),
    });
    if (!res.ok) {
      // Giữ popup để thu ngân sửa và thử lại – chưa có đơn nào được tạo
      message.error(res.error || 'Thanh toán thất bại');
      return;
    }
    if (activeDraftId) usePosStore.getState().deleteDrafts([activeDraftId]);
    setPaymentOpen(false);
    setRemoteResult({ detail: res.detail || null, orderId: res.orderId!, orderCode: res.orderCode!, change: res.change, partialError: res.partialError });
    waiting.reload();
  };

  /** Bán lẻ: tạo đơn khách lẻ và thu đủ trong một lượt trên hệ thống (API 7) */
  const confirmWalkInPayment = async (payments: PaymentLineInput[], note: string) => {
    const res = await createWalkInRemote({
      requestId: walkInRequestId.current, cart, pricing, payments, customer, branchId: saleBranch?.id ?? null, cashier: userName, note,
      approvedBy: remoteApprover,
      approvedNote: remoteApprover && remoteNeed
        ? `${APPROVER_LABEL[remoteNeed]} ${userName} duyệt CK ${pricing.manualPercent.toFixed(1)}% tại POS`
        : undefined,
    });
    if (!res.ok) {
      // Giữ popup để thu ngân sửa và thử lại – chưa có đơn nào được tạo
      message.error(res.error || 'Thanh toán thất bại');
      return;
    }
    if (activeDraftId) usePosStore.getState().deleteDrafts([activeDraftId]);
    setPaymentOpen(false);
    setRemoteResult({ detail: res.detail || null, orderId: res.orderId!, orderCode: res.orderCode!, change: res.change, partialError: res.partialError });
  };

  const confirmPayment = async (entered: PaymentLineInput[]) => {
    // Chia đợt (chỉ đơn lịch hẹn): lúc tạo đơn chỉ thu đợt 1, kế hoạch đợt gửi lên API (installments[])
    const payments = plan ? capPayments(entered, plan[0].amount) : entered;
    const note = orderNote.trim();
    return aptMode ? confirmRemotePayment(payments, note) : confirmWalkInPayment(payments, note);
  };

  const newOrder = () => {
    setRemoteResult(null);
    resetCart();
    setMobileTab('catalog');
    setTimeout(() => searchRef.current?.focus(), 100);
  };

  // ---------------------------------------------------------------- phím tắt
  const keyHandler = useRef<(e: KeyboardEvent) => void>(() => {});
  keyHandler.current = (e: KeyboardEvent) => {
    if (anyModalOpen()) return;
    if (e.key === 'F2') { e.preventDefault(); setMobileTab('catalog'); setTimeout(() => searchRef.current?.focus(), 0); }
    else if (e.key === 'F4') { e.preventDefault(); setMobileTab('cart'); setTimeout(() => customerRef.current?.focus(), 0); }
    else if (e.key === 'F8') { e.preventDefault(); if (aptMode) updateAppointment(); else saveDraft(); }
    else if (e.key === 'F9') { e.preventDefault(); pay(); }
    else if (e.key === 'Escape') {
      if (isEditable(document.activeElement)) (document.activeElement as HTMLElement).blur();
      else cancelCart();
    }
  };
  useEffect(() => {
    const h = (e: KeyboardEvent) => keyHandler.current(e);
    window.addEventListener('keydown', h);
    return () => window.removeEventListener('keydown', h);
  }, []);

  // Nhả khóa nháp khi rời màn
  const activeDraftRef = useRef(activeDraftId);
  activeDraftRef.current = activeDraftId;
  useEffect(() => () => { if (activeDraftRef.current) usePosStore.getState().unlockDraft(activeDraftRef.current); }, []);

  // ---------------------------------------------------------------- giữ giỏ đang soạn khi rời màn
  // Sang trang khác trong POS / CMS rồi quay lại (hoặc tải lại trang) vẫn còn giỏ – lưu theo tab (sessionStorage).
  // Không giữ phê duyệt CK (phải nhập PIN lại); xóa ngay khi đã thu tiền để không khôi phục giỏ đã thanh toán.
  const [hydrated, setHydrated] = useState(false);
  useEffect(() => {
    const restore = async () => {
      const s = readCartSession();
      if (!s || s.branchId !== branchId || !s.cart.length) return;
      // Lịch hẹn có thể đã được thu ở máy khác trong lúc rời màn
      if (s.activeApt?.remote) {
        const paid = await verifyPosCheckin({ appointment_id: Number(s.activeApt.id) });
        if (paid.ok && paid.data) {
          clearCartSession();
          message.warning(`Lịch hẹn ${s.activeApt.code} đã được thu ở đơn ${paid.data.order_code} – bỏ giỏ đang soạn`);
          return;
        }
      }
      // Nháp đang mở: khóa lại nếu chưa ai khác mở trong lúc rời màn
      const draft = s.activeDraftId ? usePosStore.getState().drafts.find(d => d.id === s.activeDraftId) : undefined;
      const keepDraft = !!draft && !draftLockInfo(draft, userName).locked;
      if (keepDraft) lockDraft(draft!.id, userName);
      setCart(s.cart);
      setCustomer(s.customer);
      setOrderNote(s.orderNote);
      setShowOrderNote(!!s.orderNote);
      setPlan(s.plan);
      setAptOriginal(s.aptOriginal);
      setAptSavedNote(s.aptSavedNote || '');
      setActiveApt(s.activeApt);
      setActiveDraftId(keepDraft ? draft!.id : null);
      setLeftMode(s.activeApt ? 'waiting' : 'catalog');
      setMobileTab('cart');
      message.info('Đã khôi phục giỏ hàng đang soạn');
    };
    restore().finally(() => setHydrated(true));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (!hydrated) return;
    if (!cart.length || remoteResult) { clearCartSession(); return; }
    writeCartSession({
      branchId, cart, customer, orderDiscount, voucherCode, orderNote, plan, aptOriginal, aptSavedNote, activeApt, activeDraftId,
    });
  }, [hydrated, branchId, cart, customer, orderDiscount, voucherCode, orderNote, plan, aptOriginal, aptSavedNote, activeApt, activeDraftId, remoteResult]);

  // Đóng tab / tải lại khi giỏ còn hàng → trình duyệt hỏi lại (đóng tab là mất phiên)
  const hasOpenCart = cart.length > 0 && !remoteResult;
  useEffect(() => {
    if (!hasOpenCart) return;
    const h = (e: BeforeUnloadEvent) => { e.preventDefault(); e.returnValue = ''; };
    window.addEventListener('beforeunload', h);
    return () => window.removeEventListener('beforeunload', h);
  }, [hasOpenCart]);

  const onSearchEnter = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key !== 'Enter') return;
    const q = query.trim().toLowerCase();
    if (!q) return;
    // Quét mã vạch / gõ đúng mã hàng → thêm ngay
    const exact = RETAIL.find(c => c.code.toLowerCase() === q || (!!c.barcode && c.barcode.toLowerCase() === q));
    const list = exact ? [exact] : RETAIL.filter(c => matches(c, q));
    if (list.length === 1) { addItem(list[0]); setQuery(''); }
    else if (!list.length) message.warning('Không tìm thấy hàng phù hợp');
  };

  const payBlockedReason = !cart.length ? 'Giỏ hàng trống' : !online ? 'Mất kết nối' : null;
  // Lý do chưa thanh toán được, hiện ngay trên nút (thay vì chỉ báo lỗi sau khi bấm); blocking = nút bị khóa
  const unpricedLine = cart.find(l => l.aptServiceId && l.listPrice <= 0);
  const planIssue = plan ? planBlockedReason || planError(plan, totals.total) : null;
  const overStockLines = cart.filter(l => { const st = stockOf(l); return st !== null && qtyInCart(l.itemId) > st; });
  const overStockLine = overStockLines[0];
  const overStockText = overStockLines.length > 1
    ? `Vượt tồn kho: ${overStockLines.map(l => `${l.name} (còn ${stockOf(l)})`).join(', ')}`
    : overStockLine ? `Vượt tồn kho: ${overStockLine.name} (còn ${stockOf(overStockLine)})` : '';
  const payIssue: { text: string; blocking: boolean; action?: { label: string; run: () => void } } | null =
    !cart.length ? null
      : remoteApprovalBlocked ? { text: remoteApprovalMsg, blocking: true }
      : !aptMode && !saleBranch ? { text: 'Chọn chi nhánh bán (cạnh ô tìm sản phẩm) để ghi nhận đơn', blocking: true }
      : !aptMode && !customer && cart.some(l => l.type === 'package') ? {
        text: 'Gói liệu trình cần chọn khách hàng để phát hành thẻ', blocking: true,
        action: { label: 'Chọn khách', run: () => { setMobileTab('cart'); customerRef.current?.focus(); } },
      }
      : packageCapMsg ? { text: packageCapMsg, blocking: true }
      : overStockLine && config.blockOutOfStock ? { text: overStockText, blocking: true }
      : unpricedLine ? { text: `Nhập đơn giá cho “${unpricedLine.name}” trước khi thanh toán`, blocking: true }
      : planIssue ? { text: `Chia đợt thanh toán: ${planIssue}`, blocking: true }
      : !planApprovalOk ? {
        text: `Đợt 1 chỉ ${planFirstPct}% – dưới ${MIN_FIRST_PCT}% cần Quản lý duyệt: tăng đợt 1 hoặc đăng nhập tài khoản Quản lý (có gắn nhân viên)`,
        blocking: true,
      }
      : overStockLine ? { text: `${overStockText} – kiểm tra kho trước khi giao`, blocking: false }
      : null;
  const removedAptLines = aptMode ? aptOriginal.filter(o => !cart.some(l => l.aptServiceId === o.aptServiceId)) : [];

  // ================================================================ giao diện
  return (
    <ConfigProvider theme={posTheme}>
      <Helmet><title>Bán hàng (POS) | Khothi</title></Helmet>
      {modalCtx}
      <div className="flex h-[100dvh] flex-col overflow-hidden bg-slate-100 text-slate-800">
        <style>{`
          @keyframes posFlash { 0% { background-color: rgba(62,145,171,.18); } 100% { background-color: #fff; } }
          .pos-flash { animation: posFlash 1s ease-out; }
          .pos-scroll::-webkit-scrollbar { width: 6px; } .pos-scroll::-webkit-scrollbar-thumb { background: #cbd5e1; border-radius: 4px; }
          input[type=number]::-webkit-inner-spin-button { -webkit-appearance: none; margin: 0; }
        `}</style>

        {/* HEADER */}
        <header className="relative z-10 flex shrink-0 items-center justify-between gap-2 border-b border-slate-200 bg-white px-3 py-2 shadow-sm sm:px-4">
          <div className="flex min-w-0 items-center gap-2 sm:gap-3">
            <Dropdown
              trigger={['click']}
              menu={{
                items: [
                  { key: '/pos/dispatch', label: 'Điều phối phòng' },
                  { key: '/pos/orders', label: 'Đơn hàng' }, { key: '/pos/drafts', label: 'Đơn nháp' },
                  { key: '/pos/transactions', label: 'Lịch sử giao dịch' }, { key: '/pos/refunds', label: 'Hoàn tiền' },
                  { key: '/pos/promotions', label: 'Khuyến mãi' }, { key: '/pos/import', label: 'Import MySpa' },
                  { key: '/pos/dashboard', label: 'Thống kê' }, { type: 'divider' }, { key: '/', label: '← Về CMS' },
                ],
                onClick: ({ key }) => navigate(key),
              }}
            >
              <button className="flex h-10 w-10 shrink-0 items-center justify-center rounded-lg border border-slate-200 text-slate-600 hover:bg-slate-50" aria-label="Menu POS">
                <LayoutGrid className="h-5 w-5" />
              </button>
            </Dropdown>
            <img src="https://khothi-cms.buso.asia/images/logo-v.png" alt="Khothi" className="hidden h-8 w-auto shrink-0 sm:block" />
            <h1 className="hidden border-l border-slate-300 pl-3 text-lg font-bold lg:block">Bán hàng</h1>
            {/* Chi nhánh làm việc – chỉ hiển thị (không đổi tại POS) */}
            <span title="Chi nhánh làm việc" className="flex h-10 shrink-0 items-center gap-1.5 rounded-lg bg-slate-100 px-3 text-sm font-semibold text-slate-700">
              <MapPin className="h-4 w-4 text-[#3e91ab]" /> {workingBranchName}
            </span>
            <button onClick={() => setDraftsOpen(true)} className="flex h-10 shrink-0 items-center gap-1.5 rounded-lg bg-slate-100 px-3 text-sm font-semibold text-slate-700 hover:bg-slate-200">
              <FileText className="h-4 w-4 text-slate-500" /> <span className="hidden sm:inline">Đơn nháp</span> ({branchDrafts.length})
            </button>
            <button onClick={() => { setLeftMode('waiting'); setMobileTab('catalog'); }} title="Khách đã xác nhận làm, chờ thanh toán"
              className={`relative flex h-10 shrink-0 items-center gap-1.5 rounded-lg px-3 text-sm font-semibold ${waitingCount ? 'bg-amber-50 text-amber-800 ring-1 ring-amber-200 hover:bg-amber-100' : 'bg-slate-100 text-slate-700 hover:bg-slate-200'}`}>
              <ReceiptText className={`h-4 w-4 ${waitingCount ? 'text-amber-600' : 'text-slate-500'}`} />
              <span className="hidden sm:inline">Chờ thanh toán</span> ({waitingCount})
              {waitingCount > 0 && <span className="absolute -right-1 -top-1 h-2.5 w-2.5 animate-pulse rounded-full bg-amber-500" />}
            </button>
            <button onClick={() => setLookupOpen(true)} title="Tra đơn trên hệ thống theo mã, thu nợ / thu thêm"
              className="flex h-10 shrink-0 items-center gap-1.5 rounded-lg bg-slate-100 px-3 text-sm font-semibold text-slate-700 hover:bg-slate-200">
              <HandCoins className="h-4 w-4 text-slate-500" /> <span className="hidden md:inline">Tra đơn / Thu nợ</span>
            </button>
            <span title={online ? 'Đã đồng bộ' : 'Mất kết nối'} className={`flex shrink-0 items-center gap-1.5 whitespace-nowrap text-sm ${online ? 'text-slate-500' : 'font-semibold text-red-600'}`}>
              {online ? <Wifi className="h-4 w-4 text-green-500" /> : <WifiOff className="h-4 w-4" />}
              <span className="hidden xl:inline">{online ? 'Đã đồng bộ' : 'Mất kết nối'}</span>
            </span>
          </div>
          <div className="flex items-center gap-3">
            <div className="hidden items-center gap-3 text-sm text-slate-500 2xl:flex">
              {[['F2', 'Tìm hàng'], ['F4', 'Khách'], ['F8', 'Lưu nháp'], ['F9', 'Thanh toán'], ['Esc', 'Hủy']].map(([k, l]) => (
                <span key={k} className="flex items-center gap-1"><kbd className="rounded border border-slate-300 bg-slate-100 px-1.5 py-0.5 font-mono text-xs text-slate-600">{k}</kbd>{l}</span>
              ))}
            </div>
            <PosPayRequests />
            <div className="hidden xl:block"><DemoRoleSwitch /></div>
            <div className="hidden whitespace-nowrap text-right leading-tight lg:block">
              <div className="text-sm font-semibold text-slate-700">{userName}</div>
              <div className="text-xs text-slate-500">{ROLE_LABEL[role]}</div>
            </div>
            <div className="flex shrink-0 items-center rounded-lg bg-slate-100 p-1 lg:hidden">
              <button onClick={() => setMobileTab('catalog')} className={`flex h-9 items-center gap-1 rounded-md px-3 text-sm font-semibold ${mobileTab === 'catalog' ? 'bg-white text-[#2c6b7f] shadow' : 'text-slate-600'}`}>
                <Grid className="h-4 w-4" /> Hàng
              </button>
              <button onClick={() => setMobileTab('cart')} className={`relative flex h-9 items-center gap-1 rounded-md px-3 text-sm font-semibold ${mobileTab === 'cart' ? 'bg-white text-[#2c6b7f] shadow' : 'text-slate-600'}`}>
                <ShoppingCart className="h-4 w-4" /> Giỏ ({totalQty})
              </button>
            </div>
          </div>
        </header>

        {!online && (
          <div className="flex shrink-0 items-center justify-center gap-2 bg-red-600 px-3 py-2 text-sm font-semibold text-white">
            <WifiOff className="h-4 w-4" /> Mất kết nối – chưa thể thanh toán. Vẫn có thể thêm hàng và lưu nháp.
          </div>
        )}

        <main className="relative flex min-h-0 flex-1 overflow-hidden">
          {/* ===== DANH MỤC ===== */}
          <section className={`min-h-0 w-full flex-col border-r border-slate-200 lg:flex lg:w-7/12 ${mobileTab === 'catalog' ? 'flex' : 'hidden'}`}>
            <div role="tablist" className="flex shrink-0 gap-1 border-b border-slate-200 bg-white px-3 pt-2">
              {([['waiting', `Khách chờ thanh toán (${waitingCount})`], ['catalog', 'Bán lẻ – chọn hàng']] as const).map(([k, label]) => (
                <button key={k} role="tab" aria-selected={leftMode === k} onClick={() => setLeftMode(k)}
                  className={`-mb-px flex h-11 items-center gap-1.5 border-b-2 px-4 text-sm font-semibold transition ${leftMode === k ? 'border-[#357f96] text-[#2c6b7f]' : 'border-transparent text-slate-500 hover:text-slate-700'}`}>
                  {k === 'waiting' ? <ReceiptText className="h-4 w-4" /> : <Grid className="h-4 w-4" />}{label}
                </button>
              ))}
            </div>
            {leftMode === 'waiting' ? (
              <WaitingPanel items={waiting.items} loading={waiting.loading} error={waiting.error} lastLoaded={waiting.lastLoaded}
                onReload={waiting.reload} activeId={activeApt?.id || null} pickingId={pickingId} onPick={pickAppointment} />
            ) : (<>
            {/* Bán lẻ chỉ có sản phẩm: tìm kiếm + danh mục chung một hàng */}
            <div className="z-10 flex shrink-0 flex-wrap items-center gap-2 border-b border-slate-200 bg-white p-3 shadow-sm">
              <div className="relative min-w-[220px] flex-1">
                <span className="pointer-events-none absolute inset-y-0 left-3 flex items-center"><Search className="h-5 w-5 text-slate-400" /></span>
                <input ref={searchRef} value={query} onChange={e => setQuery(e.target.value)} onKeyDown={onSearchEnter}
                  placeholder="Tìm tên, mã sản phẩm, quét mã vạch… (F2)" aria-label="Tìm sản phẩm"
                  className="h-11 w-full rounded-lg border border-slate-300 bg-white pl-11 pr-10 text-base outline-none focus:border-[#3e91ab] focus:ring-2 focus:ring-[#3e91ab]/20" />
                {query && (
                  <button onClick={() => { setQuery(''); searchRef.current?.focus(); }} className="absolute inset-y-0 right-1 flex w-10 items-center justify-center text-slate-400 hover:text-slate-600" aria-label="Xóa từ khóa">
                    <X className="h-5 w-5" />
                  </button>
                )}
              </div>
              {/* API sản phẩm chưa có danh mục: chỗ này chọn chi nhánh hệ thống ghi nhận đơn bán lẻ */}
              <Select<number> value={saleBranch?.id} onChange={chooseSaleBranch} aria-label="Chi nhánh bán"
                placeholder={<span className="flex items-center gap-1.5"><MapPin className="h-4 w-4" /> Chọn chi nhánh bán</span>}
                popupMatchSelectWidth={false} className="!h-11 w-56" status={!saleBranch && cart.length && !aptMode ? 'warning' : undefined}
                notFoundContent="Không tải được chi nhánh"
                labelRender={o => <span className="flex items-center gap-1.5"><MapPin className="h-4 w-4 text-[#3e91ab]" /> {o.label}</span>}
                options={serverBranches.map(b => ({ value: b.id, label: b.name }))} />
            </div>

            {/* Đang thu tiền lịch hẹn: danh mục không dùng được – báo trước thay vì để bấm rồi mới lỗi */}
            {aptMode && (
              <div className="flex shrink-0 flex-wrap items-center gap-2 border-b border-amber-200 bg-amber-50 px-3 py-2 text-sm text-amber-900" role="status">
                <Stethoscope className="h-4 w-4 shrink-0" />
                <span className="min-w-0 flex-1">Đang thu tiền lịch hẹn <b className="font-mono">{activeApt?.code}</b> – chỉ chỉnh dịch vụ của lịch hẹn. Thoát đơn để bán lẻ.</span>
                <button onClick={() => setLeftMode('waiting')} className="h-9 rounded-lg px-3 font-semibold text-amber-900 hover:bg-amber-100">Về khách chờ</button>
                <button onClick={() => exitAppointment()} className="h-9 rounded-lg bg-white px-3 font-semibold text-amber-900 ring-1 ring-amber-300 hover:bg-amber-100">Thoát đơn</button>
              </div>
            )}
            <div className={`pos-scroll min-h-0 flex-1 overflow-y-auto p-3 sm:p-4 ${aptMode ? 'pointer-events-none select-none opacity-40' : ''}`} aria-disabled={aptMode || undefined}>
              {loading ? (
                <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 xl:grid-cols-4">
                  {Array.from({ length: 8 }).map((_, i) => <div key={i} className="h-28 animate-pulse rounded-xl bg-white" />)}
                </div>
              ) : products.error || !RETAIL.length ? (
                <div className="flex flex-col items-center justify-center gap-3 py-16 text-center text-slate-500">
                  <ShoppingBasket className="h-10 w-10 text-slate-300" />
                  <p>{products.error || 'Chưa có sản phẩm đang kinh doanh trên hệ thống'}</p>
                  <p className="max-w-sm text-sm text-slate-400">{products.error ? 'Kiểm tra kết nối rồi tải lại.' : 'Thêm sản phẩm ở mục Quản lý sản phẩm (trạng thái Đang kinh doanh) rồi tải lại.'}</p>
                  <button onClick={products.reload} className="flex h-10 items-center gap-1.5 rounded-lg border border-slate-300 bg-white px-4 text-sm font-semibold hover:bg-slate-50"><RotateCcw className="h-4 w-4" /> Tải lại</button>
                </div>
              ) : filtered.length === 0 ? (
                <div className="flex flex-col items-center justify-center gap-3 py-16 text-slate-500">
                  <Search className="h-10 w-10 text-slate-300" />
                  <p>Không tìm thấy hàng phù hợp</p>
                  <button onClick={() => setQuery('')} className="h-10 rounded-lg border border-slate-300 bg-white px-4 text-sm font-semibold hover:bg-slate-50">Xóa từ khóa</button>
                </div>
              ) : (
                <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 xl:grid-cols-4">
                  {filtered.map(item => {
                    const stock = stockOf(item);
                    const out = stock !== null && stock <= 0;
                    const badge = item.badge;
                    return (
                      <button key={item.id} onClick={() => addItem(item)}
                        disabled={out && config.blockOutOfStock}
                        className={`relative flex h-28 flex-col justify-between rounded-xl border border-slate-200 bg-white p-3 text-left transition hover:border-[#3e91ab] hover:shadow-md active:bg-[#ecf6f9] ${out ? 'opacity-60' : ''}`}>
                        {badge && <span className="absolute -right-1.5 -top-1.5 rounded-full bg-red-500 px-2 py-0.5 text-[11px] font-bold text-white shadow-sm">{badge}</span>}
                        <div>
                          <h3 className="line-clamp-2 text-sm font-semibold leading-snug text-slate-800 sm:text-[15px]">{item.name}</h3>
                          <span className="mt-1 inline-block rounded bg-slate-100 px-1.5 font-mono text-[11px] font-medium text-slate-500">{item.code}{item.group ? ` · ${item.group}` : ''}</span>
                        </div>
                        <div className="flex items-end justify-between">
                          <span className="text-sm font-bold text-[#2c6b7f] sm:text-base">{money(item.price)}</span>
                          {stock !== null && <span className={`text-xs font-medium ${out ? 'text-red-500' : 'text-slate-400'}`}>Tồn {stock}</span>}
                        </div>
                      </button>
                    );
                  })}
                </div>
              )}
            </div>

            </>)}

            <div className="shrink-0 border-t border-slate-200 bg-white p-2.5 shadow-lg lg:hidden">
              <button onClick={() => setMobileTab('cart')} className="flex h-12 w-full items-center justify-between rounded-lg bg-[#357f96] px-4 font-bold text-white">
                <span className="flex items-center gap-2"><ShoppingBasket className="h-5 w-5" /> Xem giỏ hàng ({totalQty})</span>
                <span className="text-lg">{money(totals.total)}đ</span>
              </button>
            </div>
          </section>

          {/* ===== GIỎ HÀNG ===== */}
          <section className={`min-h-0 w-full flex-col bg-white lg:flex lg:w-5/12 ${mobileTab === 'cart' ? 'flex' : 'hidden'}`}>
            <div className="flex shrink-0 items-center justify-between border-b border-slate-200 bg-slate-100 p-2 lg:hidden">
              <button onClick={() => setMobileTab('catalog')} className="flex h-10 items-center gap-1 px-2 text-sm font-semibold text-slate-600"><ArrowLeft className="h-4 w-4" /> Quay lại danh mục</button>
              <span className="pr-2 text-sm font-bold text-slate-700">Giỏ hàng ({totalQty})</span>
            </div>

            <div className="shrink-0 border-b border-slate-200 bg-slate-50 p-3">
              {aptMode ? (
                <div className="flex min-h-[44px] items-center gap-2 rounded-lg border border-[#3e91ab]/40 bg-white px-3 py-1.5" title="Khách theo lịch hẹn">
                  <User className="h-5 w-5 shrink-0 text-[#3e91ab]" />
                  <div className="min-w-0 flex-1">
                    <div className="flex flex-wrap items-center gap-x-2 text-sm">
                      <span className="font-bold text-slate-800">{activeApt?.customer?.name || 'Khách lẻ'}</span>
                      {activeApt?.customer?.phone && <span className="text-slate-500">{formatPhone(activeApt.customer.phone)}</span>}
                    </div>
                    <div className="flex flex-wrap items-center gap-x-2 text-xs text-slate-500">
                      {activeApt?.customer?.rank && <span className="flex items-center gap-0.5 font-semibold"><Crown className="h-3 w-3" />{activeApt.customer.rank}</span>}
                      <span>Nợ <b className={(activeApt?.customer?.debt || 0) > 0 ? 'text-red-600' : 'text-slate-700'}>{money(activeApt?.customer?.debt || 0)}đ</b></span>
                      {activeApt?.customer?.wallet != null && <><span>·</span><span>Ví <b className="text-slate-700">{money(activeApt.customer.wallet)}đ</b></span></>}
                      {activeApt?.branch?.name && <><span>·</span><span>{activeApt.branch.name}</span></>}
                    </div>
                  </div>
                </div>
              ) : (
                <CustomerPicker remote value={customer} onChange={setCustomer} inputRef={customerRef} debtOf={debtOf}
                  badge={activeDraftId ? (
                    <span title={`Đang mở nháp: ${activeDraftName} – Lưu nháp (F8) để cập nhật`}
                      className="flex max-w-[11rem] shrink-0 items-center gap-1 rounded-md bg-[#ecf6f9] px-2 py-1 text-xs font-semibold text-[#2c6b7f]">
                      <FileText className="h-3.5 w-3.5 shrink-0" />
                      {/* Tên nháp mặc định "Tên khách – giờ": bỏ phần tên khách đã hiện bên trái */}
                      <span className="truncate">Nháp · {customer && activeDraftName.startsWith(`${customer.name} – `) ? activeDraftName.slice(customer.name.length + 3) : activeDraftName}</span>
                    </span>
                  ) : undefined} />
              )}
              <PosCardDebt customerId={customer?.id || null} />
              {activeApt && aptDiff && (
                <div className="mt-2 space-y-1.5 rounded-lg border border-amber-200 bg-amber-50/60 p-2">
                  <div className="flex items-center justify-between gap-2 px-1 text-xs text-amber-900">
                    <span className="flex min-w-0 items-center gap-1.5">
                      <Stethoscope className="h-3.5 w-3.5 shrink-0" />
                      <span className="truncate">Thu tiền lịch hẹn <b className="font-mono">{activeApt.code}</b>{activeApt.consultant ? ` · ${/^tư vấn/i.test(activeApt.consultant) ? activeApt.consultant : `tư vấn ${activeApt.consultant}`}` : ''}</span>
                    </span>
                    {/* Đơn lịch hẹn rời bằng nút "Thoát đơn" phía dưới (Esc) */}
                    {!aptMode && (
                      <button onClick={() => setActiveApt(null)} className="-my-1 h-8 shrink-0 rounded-md px-2.5 font-semibold text-amber-800 hover:bg-amber-100" title="Đơn sẽ không gắn với lịch hẹn">Bỏ liên kết</button>
                    )}
                  </div>
                  <AppointmentDiff diff={aptDiff} />
                  {removedAptLines.length > 0 && (
                    <div className="flex flex-wrap items-center gap-1.5 px-1 text-xs text-slate-600">
                      <span>Đã bỏ:</span>
                      {removedAptLines.map(l => (
                        <button key={l.key} onClick={() => restoreLine(l, aptOriginal.indexOf(l))} title={`Khôi phục ${l.name}`}
                          className="inline-flex h-8 max-w-full items-center gap-1 rounded-lg border border-slate-300 bg-white px-2.5 font-semibold text-[#2c6b7f] hover:border-[#3e91ab] hover:bg-[#ecf6f9]">
                          <RotateCcw className="h-3.5 w-3.5 shrink-0" /><span className="truncate">Khôi phục {l.name}</span>
                        </button>
                      ))}
                    </div>
                  )}
                </div>
              )}
            </div>

            {/* Danh sách luôn giữ tối thiểu ~1,5 thẻ dịch vụ; phần tổng bên dưới tự cuộn khi chật */}
            <div className="pos-scroll min-h-[11rem] flex-1 overflow-y-auto px-3 py-2">
              {cart.length === 0 ? (
                <div className="flex h-full flex-col items-center justify-center gap-2 py-8 text-slate-400">
                  <ShoppingBasket className="h-12 w-12 text-slate-200" />
                  <p className="text-base">Giỏ hàng trống</p>
                  <p className="text-sm">Chọn dịch vụ / sản phẩm để bắt đầu</p>
                </div>
              ) : (
                // Ô chọn trong thẻ dòng cao 36px (gọn hơn mặc định POS 40px, vẫn đủ bấm cảm ứng)
                <ConfigProvider theme={{ components: { Select: { controlHeight: 36 } } }}>
                <div className="space-y-2">
                  {cart.map(line => {
                    const priced = pricing.lines.find(l => l.key === line.key)!;
                    const stock = stockOf(line);
                    const over = stock !== null && qtyInCart(line.itemId) > stock;
                    return (
                      <div key={line.key} data-line-key={line.key}
                        className={`relative rounded-xl border bg-white px-3 py-2.5 ${over ? 'border-red-300 ring-1 ring-red-200' : 'border-slate-200'} ${flashKey === line.key ? 'pos-flash' : ''}`}>
                        <button onClick={() => removeLine(line.key)} className="absolute right-1 top-1 flex h-8 w-8 items-center justify-center rounded-lg text-slate-400 hover:bg-red-50 hover:text-red-500" aria-label="Xóa dòng">
                          <X className="h-4 w-4" />
                        </button>
                        <div className="mb-2 flex items-start justify-between gap-2 pr-7">
                          <div className="min-w-0">
                            {/* Tên dài gọn 1 dòng để danh sách thấy được nhiều dòng; bấm để xem đủ */}
                            <h4 title={line.name} onClick={() => setExpandedNames(n => ({ ...n, [line.key]: !n[line.key] }))}
                              className={`cursor-pointer text-sm font-semibold leading-snug text-slate-800 sm:text-base ${expandedNames[line.key] ? '' : 'line-clamp-1'}`}>{line.name}</h4>
                            {line.aptServiceId && !CATALOG.some(c => c.id === line.itemId) && line.listPrice <= 0 ? (
                              <label className="mt-1 flex flex-wrap items-center gap-1.5 text-xs text-amber-700">
                                <AlertTriangle className="h-3.5 w-3.5 shrink-0" /> Lịch hẹn chưa có giá – đơn giá
                                <input type="number" min={0} placeholder="0" aria-label={`Đơn giá ${line.name}`}
                                  onBlur={e => { const v = Math.max(0, Math.round(Number(e.target.value) || 0)); if (v) updateLine(line.key, { listPrice: v }); }}
                                  onKeyDown={e => { if (e.key === 'Enter') (e.target as HTMLInputElement).blur(); }}
                                  className="h-7 w-28 rounded border border-amber-300 px-2 text-right text-xs text-slate-800 outline-none focus:border-[#3e91ab]" />
                              </label>
                            ) : (
                              <div className="flex flex-wrap items-center gap-x-1.5 text-xs text-slate-500">
                                <span>{money(line.listPrice)} · {line.aptServiceId && !CATALOG.some(c => c.id === line.itemId) ? 'Dịch vụ lịch hẹn' : TYPE_LABEL[line.type]}</span>
                                {/* Cảnh báo tồn chung dòng giá cho thẻ gọn */}
                                {over && <span className="flex items-center font-medium text-red-500"><AlertTriangle className="mr-0.5 h-3 w-3 shrink-0" />Tồn {stock} – vượt tồn</span>}
                              </div>
                            )}                          </div>
                          <div className="shrink-0 text-right">
                            <div className="text-sm font-bold text-slate-800 sm:text-base">{money(priced.afterLine)}</div>
                            {priced.afterLine !== priced.gross && <div className="text-xs text-slate-400 line-through">{money(priced.gross)}</div>}
                          </div>
                        </div>
                        <div className="flex flex-wrap items-center gap-2">
                          <div className="flex h-9 w-28 items-center rounded-lg border border-slate-300 bg-white">
                            <button onClick={() => setLineQty(line, line.qty - 1)} className="flex h-full w-9 shrink-0 items-center justify-center rounded-l-lg text-slate-500 hover:bg-slate-100" aria-label="Giảm số lượng"><Minus className="h-4 w-4" /></button>
                            {/* Gõ 0 không xóa dòng – xóa bằng nút − hoặc ✕ (có Hoàn tác) */}
                            <input type="number" min={1} max={999} value={line.qty} aria-label="Số lượng"
                              onChange={e => { const v = Number(e.target.value); if (e.target.value !== '' && v >= 1) setLineQty(line, v); }}
                              className="h-full min-w-0 flex-1 border-none p-0 text-center text-base font-semibold outline-none" />
                            <button onClick={() => setLineQty(line, line.qty + 1)} className="flex h-full w-9 shrink-0 items-center justify-center rounded-r-lg text-slate-500 hover:bg-slate-100" aria-label="Tăng số lượng"><Plus className="h-4 w-4" /></button>
                          </div>
                          <div className="flex h-9 w-36 items-center overflow-hidden rounded-lg border border-slate-300 bg-white text-sm">
                            <Select<DiscountType> variant="borderless" value={line.discountType} aria-label="Kiểu chiết khấu dòng"
                              onChange={v => switchLineDiscountType(line, v)}
                              options={DISCOUNT_TYPE_OPTIONS} popupMatchSelectWidth={false}
                              className="h-full w-14 shrink-0 border-r border-slate-300 bg-slate-50" />
                            <DiscountValueInput type={line.discountType} value={line.discountValue} placeholder="CK" aria-label="Chiết khấu dòng" className="h-full"
                              onChange={v => setLineDiscount(line, v)} />
                          </div>
                          {aptMode ? (
                            <Select allowClear showSearch filterOption={viFilterOption} aria-label="Nhân viên thực hiện"
                              placeholder="Chọn KTV thực hiện" notFoundContent="Không tìm thấy nhân viên"
                              value={line.staffId || undefined}
                              onChange={(v?: string) => updateLine(line.key, { staffId: v || '', staffName: staffOptions.find(s => s.id === v)?.name || '' })}
                              className="min-w-[160px] flex-1" optionRender={renderStaffOption}
                              options={[
                                ...(line.staffId && !staffOptions.some(s => s.id === line.staffId)
                                  ? [{ label: 'Đang chọn', options: [{ value: line.staffId, label: line.staffName || `NV #${line.staffId}`, dept: '' }] }] : []),
                                ...staffGroups,
                              ]} />
                          ) : (
                            // Bán lẻ tại quầy không chọn nhân viên theo từng dòng
                            <div className="flex-1" />
                          )}
                          {/* API lịch hẹn chưa có ghi chú theo dòng – dùng ghi chú đơn */}
                          {!aptMode && (
                            <button onClick={() => setOpenNotes(n => ({ ...n, [line.key]: !n[line.key] }))}
                              className={`flex h-9 w-9 shrink-0 items-center justify-center rounded-lg border ${line.note ? 'border-[#3e91ab] bg-[#ecf6f9] text-[#2c6b7f]' : 'border-slate-300 text-slate-500'}`} aria-label="Ghi chú dòng">
                              <StickyNote className="h-4 w-4" />
                            </button>
                          )}
                        </div>
                        {!aptMode && (openNotes[line.key] || line.note) && (
                          <input value={line.note} maxLength={200} onChange={e => updateLine(line.key, { note: e.target.value })} placeholder="Ghi chú cho dòng (tối đa 200 ký tự)" aria-label={`Ghi chú dòng ${line.name}`}
                            className="mt-2 h-9 w-full rounded-lg border border-slate-300 px-3 text-sm outline-none focus:border-[#3e91ab]" />
                        )}
                        {(priced.promoAmount > 0 || priced.lineDiscountAmount > 0) && (
                          <div className="mt-1.5 flex flex-wrap gap-1.5">
                            {priced.promoAmount > 0 && <span className="inline-flex items-center rounded border border-green-100 bg-green-50 px-2 py-1 text-xs font-medium text-green-700"><Tag className="mr-1 h-3.5 w-3.5" />{priced.promoName} -{money(priced.promoAmount)}</span>}
                            {priced.lineDiscountAmount > 0 && (
                              <span className="inline-flex items-center rounded border border-red-100 bg-red-50 px-2 py-1 text-xs font-medium text-red-600">
                                Chiết khấu {line.discountType === '%' ? `${String(line.discountValue).replace('.', ',')}%` : ''}: -{money(priced.lineDiscountAmount)}
                              </span>
                            )}
                            {priced.gross > 0 && priced.afterLine <= 0 && (
                              <span className="inline-flex items-center rounded border border-amber-200 bg-amber-50 px-2 py-1 text-xs font-semibold text-amber-800"><AlertTriangle className="mr-1 h-3.5 w-3.5" />Dòng này miễn phí (giảm 100%)</span>
                            )}
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
                </ConfigProvider>
              )}
            </div>

            {/* TỔNG & NÚT */}
            <div className="flex min-h-0 flex-col border-t border-slate-200 bg-slate-50 shadow-[0_-4px_12px_rgba(0,0,0,0.04)]">
              <div className="pos-scroll min-h-0 flex-1 space-y-1 overflow-y-auto px-3 py-2 text-sm">
                <Row label="Tạm tính" value={money(totals.subtotal)} />
                {totals.itemPromo > 0 && <Row label="KM tự động" value={`-${money(totals.itemPromo)}`} cls="text-green-700" />}
                {totals.lineDiscount > 0 && <Row label="Chiết khấu" value={`-${money(totals.lineDiscount)}`} cls="text-red-600" />}
                {totals.orderPromo > 0 && <Row label={totals.orderPromoName || 'KM đơn'} value={`-${money(totals.orderPromo)}`} cls="text-green-700" />}
                {/* Voucher: chưa có API kiểm tra mã nên tạm ẩn (bán lẻ lẫn lịch hẹn) */}
                {!plan && aptMode && (
                  <div className="flex items-center justify-between gap-2">
                    <span className="text-slate-600">Trả góp</span>
                    <button onClick={() => setPlan(splitPlan(totals.total, 2))} disabled={!!planBlockedReason} title={planBlockedReason || undefined}
                      className="flex h-8 items-center gap-1 rounded-lg border border-slate-300 bg-white px-2 text-xs font-semibold text-[#2c6b7f] hover:border-[#3e91ab] hover:bg-[#ecf6f9] disabled:cursor-not-allowed disabled:border-slate-200 disabled:bg-slate-50 disabled:text-slate-400">
                      <CalendarClock className="h-3.5 w-3.5" /> Chia đợt thanh toán
                    </button>
                  </div>
                )}
                {totals.rounding !== 0 && <Row label="Làm tròn" value={money(totals.rounding)} cls="text-slate-500" />}
                {/* Đã duyệt thì báo ở đây; chưa duyệt → báo cạnh nút thanh toán */}
                {remoteNeed && remoteApprover && (
                  <div className="flex items-center gap-1.5 text-xs text-green-700">
                    <ShieldCheck className="h-4 w-4" /> CK {pricing.manualPercent.toFixed(1)}% – duyệt bởi {userName} ({APPROVER_LABEL[remoteNeed]})
                  </div>
                )}
                {(showOrderNote || orderNote) && (
                  <textarea ref={orderNoteRef} value={orderNote} maxLength={500} rows={2} onChange={e => setOrderNote(e.target.value)}
                    onBlur={() => { if (!orderNote.trim()) setShowOrderNote(false); }} placeholder="Ghi chú đơn (tối đa 500 ký tự)" aria-label="Ghi chú đơn"
                    className="w-full rounded-lg border border-slate-300 px-3 py-2 text-sm outline-none focus:border-[#3e91ab]" />
                )}
                {plan && <InstallmentPlan total={totals.total} rows={plan} onChange={setPlan} onRemove={() => setPlan(null)} />}
                {plan && planNeedApproval && planApprovalOk && (
                  <div className="flex items-center gap-1.5 text-xs text-green-700">
                    <ShieldCheck className="h-4 w-4" /> Trả góp đợt 1 {planFirstPct}% – duyệt bởi {userName}
                  </div>
                )}
              </div>

              {/* TỔNG luôn hiện, không cuộn theo phần chi tiết */}
              <div className="shrink-0 border-t border-slate-200 px-3 py-1.5">
                <div className="flex items-center justify-between">
                  <span className="text-base font-bold">TỔNG</span>
                  <span className="text-2xl font-extrabold text-[#2c6b7f]">{money(totals.total)}</span>
                </div>
                {plan && (
                  <div className="flex items-center justify-between text-xs text-slate-500">
                    <span>Thu ngay (đợt 1): <b className="text-sm text-slate-800">{money(plan[0].amount)}</b></span>
                    <span>Ghi nợ theo lịch: <b className="text-amber-700">{money(totals.total - plan[0].amount)}</b></span>
                  </div>
                )}
              </div>

              {payIssue && (
                <div className={`flex shrink-0 items-center gap-2 border-t px-3 py-1.5 text-xs ${payIssue.blocking ? 'border-amber-200 bg-amber-50 text-amber-900' : 'border-amber-100 bg-amber-50/60 text-amber-800'}`} role="status">
                  <ShieldAlert className="h-4 w-4 shrink-0" />
                  <span className="min-w-0 flex-1">{payIssue.text}</span>
                  {payIssue.action && (
                    <button onClick={payIssue.action.run} className="h-9 shrink-0 rounded-lg bg-white px-3 font-semibold text-amber-900 ring-1 ring-amber-300 hover:bg-amber-100">{payIssue.action.label}</button>
                  )}
                </div>
              )}

              {/* Thanh nút: phím tắt nằm cùng hàng chữ để nút thấp gọn */}
              <div className="flex shrink-0 gap-2 border-t border-slate-200 bg-white px-3 py-2.5">
                {/* Ghi chú đơn: mở ô nhập ở phần tổng; để trống thì tự đóng khi rời ô */}
                <button onClick={() => { setShowOrderNote(true); setTimeout(() => orderNoteRef.current?.focus(), 0); }}
                  title={orderNote ? `Ghi chú đơn: ${orderNote}` : 'Thêm ghi chú đơn'} aria-label="Ghi chú đơn"
                  className={`relative flex h-12 w-12 shrink-0 items-center justify-center rounded-lg border ${orderNote ? 'border-[#3e91ab] bg-[#ecf6f9] text-[#2c6b7f]' : 'border-slate-300 bg-white text-slate-500 hover:bg-slate-100'}`}>
                  <StickyNote className="h-5 w-5" />
                  {orderNote && <span className="absolute right-1.5 top-1.5 h-2 w-2 rounded-full bg-[#3e91ab]" />}
                </button>
                {/* Đơn chờ thanh toán không lưu nháp: cập nhật thẳng lên lịch hẹn */}
                <button onClick={aptMode ? updateAppointment : saveDraft} disabled={!cart.length || updatingApt}
                  title={aptMode ? 'Lưu dịch vụ, KTV, chiết khấu, ghi chú lên lịch hẹn (F8)' : 'Lưu nháp (F8)'}
                  className="flex h-12 flex-1 items-center justify-center gap-1.5 rounded-lg border border-slate-300 bg-slate-100 text-sm font-semibold text-slate-700 hover:bg-slate-200 disabled:cursor-not-allowed disabled:opacity-50">
                  {updatingApt && <Loader2 className="h-4 w-4 animate-spin" />}
                  {aptMode ? (updatingApt ? 'Đang cập nhật…' : 'Cập nhật') : 'Lưu nháp'}
                  <kbd className="hidden rounded bg-slate-200 px-1 font-mono text-[10px] font-medium text-slate-500 xl:inline">F8</kbd>
                </button>
                {/* Đơn lịch hẹn: "Thoát đơn" chỉ rời tiến trình thu tiền (đơn vẫn chờ) – màu trung tính, không phải thao tác hủy */}
                <button onClick={cancelCart} disabled={!cart.length}
                  title={aptMode ? 'Rời đơn này để chọn đơn chờ khác – đơn vẫn ở danh sách chờ thanh toán (Esc)' : 'Hủy giỏ hàng (Esc)'}
                  className={`flex h-12 flex-1 items-center justify-center gap-1.5 rounded-lg border text-sm font-semibold disabled:cursor-not-allowed disabled:opacity-50 ${aptMode ? 'border-slate-300 bg-white text-slate-700 hover:bg-slate-100' : 'border-red-200 bg-red-50 text-red-600 hover:bg-red-100'}`}>
                  {aptMode ? <><LogOut className="h-4 w-4" /> Thoát đơn{aptDirty && <span className="h-2 w-2 rounded-full bg-amber-500" title="Có thay đổi chưa cập nhật" />}</> : <><Trash2 className="h-4 w-4" /> Hủy</>}
                  <kbd className={`hidden rounded px-1 font-mono text-[10px] font-medium xl:inline ${aptMode ? 'bg-slate-100 text-slate-400' : 'bg-red-100 text-red-400'}`}>Esc</kbd>
                </button>
                <button onClick={pay} disabled={!!payBlockedReason || !!payIssue?.blocking} title={payBlockedReason || payIssue?.text || 'Thanh toán (F9)'}
                  className="flex h-12 flex-[2] items-center justify-center gap-2 rounded-lg bg-[#357f96] text-base font-bold text-white shadow-md hover:bg-[#2c6b7f] disabled:cursor-not-allowed disabled:bg-slate-300 disabled:text-slate-500 disabled:shadow-none">
                  {payIssue?.blocking ? 'CHƯA THỂ THANH TOÁN' : plan ? `THANH TOÁN ĐỢT 1/${plan.length}` : 'THANH TOÁN'}
                  <kbd className={`hidden rounded px-1 font-mono text-[10px] font-medium xl:inline ${payIssue?.blocking ? 'bg-slate-200 text-slate-500' : 'bg-white/15 text-white/80'}`}>F9</kbd>
                </button>
              </div>
            </div>
          </section>
        </main>
      </div>

      <DraftsModal open={draftsOpen} branchId={branchId} activeDraftId={activeDraftId} onClose={() => setDraftsOpen(false)} onOpenDraft={openDraft} />
      {aptMode && activeApt ? (
        <PaymentModal open={paymentOpen} mode="sale" amountDue={collectNow} orderCode={`Lịch hẹn ${activeApt.code}`}
          customer={customerFromApi(activeApt.customer)} methods={remoteMethods(activeApt.customer?.wallet != null)} qrContent={`LH${activeApt.code}`}
          hint={planHint || undefined}
          // Trả góp: phần chưa thu đã nằm trong lịch các đợt, không ghi nợ thêm – thiếu tiền thì sửa kế hoạch chia đợt
          allowDebt={!plan}
          fullPayNote={{ hint: 'Trả góp – thu đủ đợt 1 bằng một phương thức. Khách thiếu tiền: quay lại sửa kế hoạch chia đợt.', block: 'Còn thiếu tiền – phải thu đủ đợt 1' }}
          onClose={() => setPaymentOpen(false)} onConfirm={confirmPayment} />
      ) : (
        // Bán lẻ sản phẩm: thu đủ một lần – không chia đợt, không ghi nợ; không có ví trả trước (API bán lẻ chưa hỗ trợ)
        <PaymentModal open={paymentOpen} mode="sale" amountDue={totals.total} orderCode={`Đơn khách lẻ${saleBranch ? ` · ${saleBranch.name}` : ''}`}
          // Nội dung chuyển khoản riêng cho từng lượt thanh toán để thu ngân dò đúng giao dịch trong app ngân hàng
          customer={customer} methods={remoteMethods(false)} qrContent={`BL${walkInRequestId.current.replace(/-/g, '').slice(0, 8).toUpperCase()}`}
          allowDebt={false} onClose={() => setPaymentOpen(false)} onConfirm={confirmPayment} />
      )}
      <RemoteOrderModal open={!!remoteResult} result={remoteResult} onClose={newOrder} />
      <RemoteOrderModal open={lookupOpen} onClose={() => setLookupOpen(false)} />
    </ConfigProvider>
  );
}

const Row = ({ label, value, cls = '' }: { label: string; value: string; cls?: string }) => (
  <div className={`flex justify-between gap-2 ${cls}`}><span className={cls ? '' : 'text-slate-600'}>{label}</span><span className="font-medium">{value}</span></div>
);
